import { useEffect, useState } from 'react';
import { CafCorpusEntry, getCafCorpusEntry } from '../../../api/cafCorpus';
import { formatAgo } from '../format';
import { ReadBack, finishPublication, projectionPending, projectionSuperseded } from '../saveEngine';

/**
 * Controller ruling (from the Task 9 review, carried into Tasks 13/17/18):
 * an approved example can be left "projection pending" -- partly published
 * -- when its finish step failed three times at save time. There is no
 * backend sweeper to fix these up, and a one-email example (source
 * `live_override` / `sql_override`) has no other way to re-finish. So
 * whenever a caller loads an example that is still pending, and not
 * superseded by someone else's later save, it should nudge it once,
 * silently: `finishPublication` is called exactly once, then the entry is
 * re-fetched live. No toast, no loop, no retry from here.
 *
 * Shared by `useCurrentEntry` below (Task 13's drawers) and `OneEmailModal`
 * (Task 17), which loads a possibly-existing example by task id rather than
 * by an already-known entry, so it cannot use the hook itself.
 *
 * Returns the refreshed entry, or `null` when there was nothing to do
 * (already settled or superseded) or the finish/re-fetch didn't land --
 * either way the caller just keeps showing what it already had.
 */
export async function nudgeFinish(entry: CafCorpusEntry): Promise<CafCorpusEntry | null> {
  if (!projectionPending(entry) || projectionSuperseded(entry)) return null;
  await finishPublication(entry);
  try {
    return await getCafCorpusEntry(entry.id, entry.sender);
  } catch {
    return null; // The finish step ran; keep showing what we already had.
  }
}

/**
 * Rows without a compare token are refetched so every CAS write uses the live
 * document (same rule as today's withEditableEntry).
 */
export const useCurrentEntry = (entry: CafCorpusEntry) => {
  const [current, setCurrent] = useState(entry);
  const [ready, setReady] = useState(entry.compare_token !== undefined);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    let alive = true;

    // No-op (no state update) when the entry isn't pending, so an already-
    // settled entry never triggers a spurious re-render.
    const nudgeIfPending = async (fresh: CafCorpusEntry): Promise<void> => {
      const refreshed = await nudgeFinish(fresh);
      if (alive && refreshed) setCurrent(refreshed);
    };

    const load = async () => {
      let fresh = entry;
      if (entry.compare_token === undefined) {
        try {
          const server = await getCafCorpusEntry(entry.id, entry.sender);
          fresh = { ...entry, ...server };
        } catch {
          if (alive) setProblem("Couldn't load the latest version of this example. Close and try again.");
          return;
        }
        if (alive) { setCurrent(fresh); setReady(true); }
      }
      await nudgeIfPending(fresh);
    };

    void load();
    return () => { alive = false; };
  }, [entry]);

  return { entry: current, ready, problem };
};

export async function readBackEntry(
  entry: CafCorpusEntry,
  expectedVersion: number,
  matches: (fresh: CafCorpusEntry) => boolean,
): Promise<ReadBack<CafCorpusEntry>> {
  const fresh = await getCafCorpusEntry(entry.id, entry.sender);
  const version = fresh.version ?? 0;
  if (version === expectedVersion + 1 && matches(fresh)) return { state: 'landed', value: fresh };
  if (version === expectedVersion) return { state: 'not_landed' };
  return { state: 'unknown' };
}

export const whoAndWhen = (entry: CafCorpusEntry): string => {
  const when = entry.amended_at || entry.labelled_at;
  const who = entry.amended_by || entry.labelled_by;
  return [when ? formatAgo(when) : null, who ? `by ${who}` : null].filter(Boolean).join(' ');
};
